import React, { useState, useEffect, useMemo } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { BiCloudDownload } from "react-icons/bi";
import {
  getScrappedHubWiseResourceReport,
  getHubWiseResourceTyreData,
  getValidatedByResourceTyreData,
  downloadValidatedByResourceTyres,
  downloadHubWiseResourceTyres
} from "../../apis/scrappedCasingTyres.service";
import { exportScrapCasingTyresExcel } from "../../utils/exportExcel";
import toast from "react-hot-toast";
import Spinner from "../Spinner";
import CustomDetailModal from "./CustomDetailModal";
import {
  VALIDATED_TYRES_MODAL_DETAIL_COLUMNS,
  isScrapTyreStatus,
  resolveCasingTypeDisplay,
} from "../../utils/scrapConstants";

const styles = {
  overlay: "fixed inset-0 z-50 flex items-center justify-center p-4",
  backdrop: "fixed inset-0 bg-black bg-opacity-50 transition-opacity",
  modalContent: "relative bg-white rounded-xl shadow-xl w-full max-w-[1200px] overflow-hidden flex flex-col z-10 max-h-[90vh]",
  closeBtn: "absolute -top-[1px] -right-[1px] p-2 rounded-xl z-20 text-gray-900 hover:bg-gray-100 transition-colors",
  header: "flex flex-col md:flex-row justify-between items-start md:items-center p-6 border-b border-gray-200 gap-4 mt-2",
  title: "text-2xl font-bold text-green-500 w-full md:w-auto text-left",
  dateRange: "text-sm font-medium text-gray-500 bg-gray-100 px-4 py-2 rounded-lg",
  tableWrapper: "w-full overflow-x-auto overflow-y-auto flex-1 min-h-[300px]",
  table: "w-full text-left border-collapse min-w-max",
  thead: "sticky top-0 bg-white z-10 shadow-sm border-b border-gray-200",
  th: "py-5 px-6 text-[15px] font-bold text-gray-800 whitespace-nowrap",
  thCenter: "py-5 px-6 text-[15px] font-bold text-gray-800 text-center whitespace-nowrap",
  tr: "border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors",
  td: "py-5 px-6 text-[14px] font-medium text-gray-700 whitespace-nowrap",
  tdCenter: "py-5 px-6 text-[14px] text-gray-500 text-center whitespace-nowrap",
  tdBoldCenter: "py-5 px-6 text-[14px] text-gray-700 font-bold text-center whitespace-nowrap",
  noData: "py-10 text-center text-gray-500 font-medium",
  totalRow: "bg-gray-50 font-bold",
  totalTd: "py-5 px-6 text-[14px] font-bold text-gray-800 whitespace-nowrap",
  totalTdCenter: "py-5 px-6 text-[14px] font-bold text-gray-700 text-center whitespace-nowrap",
  totalTdBoldCenter: "py-5 px-6 text-[14px] font-bold text-gray-800 text-center whitespace-nowrap",
};

const formatDateDMY = (value) => {
  if (!value) return "";
  const d = new Date(`${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const ValidatedTyresModal = ({ isOpen, onClose, resourceName, resourceId, dateFrom, dateTo }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (isOpen && resourceId) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await getScrappedHubWiseResourceReport({
            resourceId,
            dateFrom,
            dateTo,
          });
          const nextRows = response?.data || [];
          setRows(Array.isArray(nextRows) ? nextRows : []);
        } catch (error) {
          console.error("Error fetching hub-wise report:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [isOpen, resourceId, dateFrom, dateTo]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        const casingA = Number(row["Casing A"]) || 0;
        const casingB = Number(row["Casing B"]) || 0;
        const casingC = Number(row["Casing C"]) || 0;
        const casingD = Number(row["Casing D"]) || 0;
        const scrap = Number(row.scrap) || 0;
        const rowTotal = Number(row.total);
        const computedTotal = Number.isFinite(rowTotal)
          ? rowTotal
          : casingA + casingB + casingC + casingD + scrap;

        acc["Casing A"] += casingA;
        acc["Casing B"] += casingB;
        acc["Casing C"] += casingC;
        acc["Casing D"] += casingD;
        acc.scrap += scrap;
        acc.total += computedTotal;
        return acc;
      },
      { "Casing A": 0, "Casing B": 0, "Casing C": 0, "Casing D": 0, scrap: 0, total: 0 }
    );
  }, [rows]);

  useEffect(() => {
    if (!selectedDetail || !resourceId) {
      setModalData([]);
      return;
    }

    const fetchDetailData = async () => {
      setModalLoading(true);
      try {
        const payload = {
          dateFrom,
          dateTo,
          resourceId,
        };

        const normalizedType = (selectedDetail.type || "").trim().toLowerCase();
        const shouldSendTyreStatus =
          normalizedType &&
          normalizedType !== "total" &&
          normalizedType !== "grand total";

        if (shouldSendTyreStatus) payload.tyreStatus = selectedDetail.type;

        const hubLabel = (selectedDetail.hubName || "").trim();
        const useSpecificHub =
          selectedDetail.hubId &&
          hubLabel &&
          hubLabel.toLowerCase() !== "all hubs";

        if (useSpecificHub) {
          payload.hubId = selectedDetail.hubId;
          payload.hubName = selectedDetail.hubName;
        }

        const response = useSpecificHub
          ? await getHubWiseResourceTyreData(payload)
          : await getValidatedByResourceTyreData(payload);
        const nextRows = response?.data || response || [];
        setModalData(Array.isArray(nextRows) ? nextRows : []);
      } catch (error) {
        console.error("Error fetching hub-wise resource tyre data:", error);
        setModalData([]);
      } finally {
        setModalLoading(false);
      }
    };

    fetchDetailData();
  }, [selectedDetail, resourceId, dateFrom, dateTo]);

  const resolveDownloadTyreRows = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.data)) return response.data;
    return [];
  };

  const handleDownloadHubDetailData = async () => {
    if (!selectedDetail || !resourceId) {
      toast.error("Open tyre details first to download");
      return;
    }

    const payload = {
      dateFrom,
      dateTo,
      resourceId,
    };

    const normalizedType = (selectedDetail.type || "").trim().toLowerCase();
    const shouldSendTyreStatus =
      normalizedType &&
      normalizedType !== "total" &&
      normalizedType !== "grand total";
    if (shouldSendTyreStatus) payload.tyreStatus = selectedDetail.type;

    const hubLabel = (selectedDetail.hubName || "").trim();
    const useHubEndpoint =
      selectedDetail.hubId &&
      hubLabel &&
      hubLabel.toLowerCase() !== "all hubs";

    if (useHubEndpoint) {
      payload.hubId = selectedDetail.hubId;
      payload.hubName = hubLabel;
    }

    setDownloadLoading(true);
    try {
      const response = useHubEndpoint
        ? await downloadHubWiseResourceTyres(payload)
        : await downloadValidatedByResourceTyres(payload);
      const list = resolveDownloadTyreRows(response);
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      const scope = useHubEndpoint ? hubLabel : "all-hubs";
      const safeTitle = `${resourceName || "resource"}-${selectedDetail.type || "data"}-${scope}`
        .replace(/[/\\?%*:|"<>]/g, "-")
        .replace(/\s+/g, "_");
      exportScrapCasingTyresExcel(list, `validated-hub-wise_${safeTitle}`, {
        tyreStatus: selectedDetail.type,
      });
      toast.success("File downloaded");
    } catch (error) {
      console.error("Download hub-wise / resource tyres failed:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  if (!isOpen) return null;

  // Scrap tyres have no casing grade, so their Casing Type cell shows "SRP".
  const isScrapDetail = isScrapTyreStatus(selectedDetail?.type);

  return (
    <div className={styles.overlay}>
      <div className={styles.backdrop} onClick={onClose}></div>

      <div className={styles.modalContent}>
        <button onClick={onClose} className={styles.closeBtn} aria-label="Close modal">
          <AiOutlineClose size={18} className="text-gray-900" />
        </button>

        <div className={styles.header}>
          <h2 className={styles.title}>
            Validated Tyres by {resourceName || "Resource"}
          </h2>
          <div className={styles.dateRange}>
            Range: <span className="text-gray-800">{formatDateDMY(dateFrom)}</span> to <span className="text-gray-800">{formatDateDMY(dateTo)}</span>
          </div>
        </div>

        <div className={styles.tableWrapper}>
          {loading ? (
            <div className="flex justify-center items-center h-[300px]">
              <Spinner />
            </div>
          ) : (
            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr>
                  <th className={styles.th}>Hub Name</th>
                  <th className={styles.thCenter}>Casing A</th>
                  <th className={styles.thCenter}>Casing B</th>
                  <th className={styles.thCenter}>Casing C</th>
                  <th className={styles.thCenter}>Casing D</th>
                  <th className={styles.thCenter}>Scrap</th>
                  <th className={styles.thCenter}>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  <>
                    {rows.map((row, index) => (
                      <tr key={index} className={styles.tr}>
                        <td className={styles.td}>{row.hubName}</td>
                        <td
                          className={`${styles.tdCenter} text-blue-600 cursor-pointer hover:underline`}
                          onClick={() => setSelectedDetail({ hubId: row.hubId || row._id, hubName: row.hubName, type: "Casing A", value: row["Casing A"] })}
                        >
                          {row["Casing A"] ?? 0}
                        </td>
                        <td
                          className={`${styles.tdCenter} text-blue-600 cursor-pointer hover:underline`}
                          onClick={() => setSelectedDetail({ hubId: row.hubId || row._id, hubName: row.hubName, type: "Casing B", value: row["Casing B"] })}
                        >
                          {row["Casing B"] ?? 0}
                        </td>
                        <td
                          className={`${styles.tdCenter} text-blue-600 cursor-pointer hover:underline`}
                          onClick={() => setSelectedDetail({ hubId: row.hubId || row._id, hubName: row.hubName, type: "Casing C", value: row["Casing C"] })}
                        >
                          {row["Casing C"] ?? 0}
                        </td>
                        <td
                          className={`${styles.tdCenter} text-blue-600 cursor-pointer hover:underline`}
                          onClick={() => setSelectedDetail({ hubId: row.hubId || row._id, hubName: row.hubName, type: "Casing D", value: row["Casing D"] })}
                        >
                          {row["Casing D"] ?? 0}
                        </td>
                        <td
                          className={`${styles.tdCenter} text-blue-600 cursor-pointer hover:underline`}
                          onClick={() => setSelectedDetail({ hubId: row.hubId || row._id, hubName: row.hubName, type: "Scrap", value: row.scrap })}
                        >
                          {row.scrap ?? 0}
                        </td>
                        <td
                          className={`${styles.tdBoldCenter} text-blue-600 cursor-pointer hover:underline`}
                          onClick={() => setSelectedDetail({ hubId: row.hubId || row._id, hubName: row.hubName, type: "Total", value: row.total })}
                        >
                          {row.total ?? 0}
                        </td>
                      </tr>
                    ))}
                    <tr className={styles.totalRow}>
                      <td className={styles.totalTd}>Total</td>
                      <td
                        className={`${styles.totalTdCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ hubName: "All Hubs", type: "Casing A", value: totals["Casing A"] })}
                      >
                        {totals["Casing A"]}
                      </td>
                      <td
                        className={`${styles.totalTdCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ hubName: "All Hubs", type: "Casing B", value: totals["Casing B"] })}
                      >
                        {totals["Casing B"]}
                      </td>
                      <td
                        className={`${styles.totalTdCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ hubName: "All Hubs", type: "Casing C", value: totals["Casing C"] })}
                      >
                        {totals["Casing C"]}
                      </td>
                      <td
                        className={`${styles.totalTdCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ hubName: "All Hubs", type: "Casing D", value: totals["Casing D"] })}
                      >
                        {totals["Casing D"]}
                      </td>
                      <td
                        className={`${styles.totalTdCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ hubName: "All Hubs", type: "Scrap", value: totals.scrap })}
                      >
                        {totals.scrap}
                      </td>
                      <td
                        className={`${styles.totalTdBoldCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ hubName: "All Hubs", type: "Total", value: totals.total })}
                      >
                        {totals.total}
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan="7" className={styles.noData}>
                      No Records Found for the selected duration
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <CustomDetailModal
        isOpen={!!selectedDetail}
        onClose={() => setSelectedDetail(null)}
        modalWidth="80vw"
        title={`Details for ${selectedDetail?.type} (${selectedDetail?.hubName || "Hub"})`}
        filterComponent={
          <button
            type="button"
            disabled={modalLoading || downloadLoading || !selectedDetail}
            className="flex items-center gap-2 bg-[#22C55E] text-white px-5 py-2.5 rounded-xl hover:bg-green-600 transition-all shadow-sm hover:shadow-md font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadHubDetailData();
            }}
          >
            <BiCloudDownload size={20} className={downloadLoading ? "animate-pulse" : ""} />
            <span>{downloadLoading ? "Preparing…" : "Download Data"}</span>
          </button>
        }
        columns={VALIDATED_TYRES_MODAL_DETAIL_COLUMNS}
        loading={modalLoading}
        data={modalData.map((d, idx) => ({
          id: idx,
          zoneName: d.zoneName || "-",
          regionName: d.regionName || "-",
          hubName: d.hubName || "-",
          stencilNo: d.stencilNo || "-",
          tyreValidationDate: d.tyreValidationDate
            ? new Date(d.tyreValidationDate).toLocaleDateString("en-GB")
            : "-",
          casingType: resolveCasingTypeDisplay(d.casingType, isScrapDetail || isScrapTyreStatus(d.status ?? d.tyreStatus)),
          validatedBy: d.validatedBy || "-",
        }))}
      />
    </div>
  );
};

export default ValidatedTyresModal;
